"use client";

import React, { useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import styles from './UserOrdersOverTimeChart.module.css'; // Import the CSS module
import { gsap } from 'gsap'; // Import gsap

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

interface UserOrdersOverTimeChartProps {
  chartLabels: string[];
  chartDataPoints: number[];
  isLoading: boolean;
}

const UserOrdersOverTimeChart: React.FC<UserOrdersOverTimeChartProps> = ({ chartLabels, chartDataPoints, isLoading }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLoading && chartDataPoints && chartDataPoints.length > 0 && chartRef.current) {
      gsap.fromTo(
        chartRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.2 }
      );
    }
  }, [isLoading, chartDataPoints]);

  const isSingleDataPoint = chartDataPoints && chartDataPoints.length === 1;

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Your Cumulative Paid Orders",
        data: chartDataPoints,
        fill: true,
        borderColor: "rgba(75, 192, 75, 0.9)", // Brighter Green line for dark theme
        backgroundColor: "rgba(75, 192, 75, 0.25)", // Slightly more opaque green fill
        tension: 0.25, // Slightly smoother tension
        pointBackgroundColor: "rgba(75, 192, 75, 0.9)",
        pointBorderColor: "#1A202C", // Match dark card background for point border
        pointHoverBackgroundColor: "#fff", // White on hover for contrast
        pointHoverBorderColor: "rgba(75, 192, 75, 1)",
        pointRadius: isSingleDataPoint ? 5 : 3, // Larger point for single data
        pointHoverRadius: isSingleDataPoint ? 7 : 5, // Larger hover point for single data
        borderWidth: 2, // Standard line width
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          color: "#E2E8F0", // Lighter gray for legend (Tailwind gray-200)
          font: { size: 12, family: 'Inter, sans-serif' }, // Consistent font
          padding: 20,
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle' as const,
        },
      },
      title: {
        display: true,
        text: "Your Cumulative Paid Orders Over Time",
        color: "#F7FAFC", // Almost white for title (Tailwind gray-100)
        font: { size: 16, weight: "600" as "600", family: 'Inter, sans-serif' }, 
        padding: { top: 10, bottom: 25 }, // Adjusted top padding
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
        backgroundColor: "rgba(45, 55, 72, 0.95)", // Darker tooltip background (Tailwind gray-700 / alpha)
        titleColor: "rgba(75, 192, 75, 1)", // Bright Green title in tooltip
        bodyColor: "#E2E8F0", // Lighter gray for body (Tailwind gray-200)
        borderColor: "rgba(75, 192, 75, 0.6)", // Green border for tooltip
        borderWidth: 1,
        padding: 12, 
        boxPadding: 4,
        cornerRadius: 6, 
        bodySpacing: 5, // Slightly more spacing
        titleFont: { weight: 'bold' as 'bold', family: 'Inter, sans-serif' },
        bodyFont: { family: 'Inter, sans-serif' },
        footerFont: { family: 'Inter, sans-serif' },
        displayColors: false, // Hide color box in tooltip, line color is clear
      },
    },
    scales: {
      x: {
        type: "time" as const,
        time: {
          unit: "day" as const,
          tooltipFormat: "MMM d, yyyy", 
          displayFormats: { day: "MMM d" },
        },
        title: { display: false },
        ticks: {
          color: "#A0AEC0", // Medium light gray for ticks (Tailwind gray-400)
          font: { family: 'Inter, sans-serif' },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7, 
        },
        grid: { display: false }, // Keep X grid lines hidden
      },
      y: {
        beginAtZero: true,
        title: { display: false },
        ticks: { 
          color: "#A0AEC0", // Medium light gray for ticks
          font: { family: 'Inter, sans-serif' },
          precision: 0, 
          stepSize: chartDataPoints && chartDataPoints.length > 0 && Math.max(...chartDataPoints) < 5 ? 1 : undefined, 
        },
        grid: {
          color: "rgba(75, 192, 75, 0.15)", // Dark theme friendly subtle green grid lines
          drawBorder: false,
        },
        ...(isSingleDataPoint && chartDataPoints[0] !== undefined && { 
          suggestedMax: chartDataPoints[0] < 2 ? chartDataPoints[0] + 2 : chartDataPoints[0] + Math.max(1, Math.ceil(chartDataPoints[0] * 0.2)) 
        })
      },
    },
    interaction: {
      mode: "nearest" as const,
      axis: "x" as const,
      intersect: false,
    },
  };

  if (isLoading) return <p className={styles.loadingText}>Loading your orders chart...</p>;
  // Error display can be handled by the parent component or a generic error boundary
  if (!isLoading && (!chartDataPoints || chartDataPoints.length === 0))
    return <p className={styles.noDataText}>You have no paid order data to display a chart.</p>;

  return (
    <div className={styles.chartCard} ref={chartRef}> {/* Changed from chartContainer to chartCard and added ref */}
      <Line options={options as any} data={chartData} />
    </div>
  );
};

export default React.memo(UserOrdersOverTimeChart); 